import { ChannelEnum } from '@c2b/muven-commons'

/**
 * Identidade visual de cada canal para exibição compacta (grids, listas).
 *
 * `mark` é o nome do arquivo em src/channel/images/marks/ - o símbolo da marca
 * em formato quadrado, não o logotipo horizontal. Canal sem arquivo cai no
 * monograma: as letras sobre a cor da marca, no mesmo tile quadrado, para a
 * coluna continuar uniforme.
 *
 * `accent` é a luz das composições grandes (hero da tela de configurações):
 * só existe quando a cor da marca é escura demais para tingir um fundo
 * (TikTok, Amazon) ou quando a marca tem um segundo tom que a identifica melhor.
 */
export interface ChannelBrand {
    mark?: string
    color: string
    textColor: string
    monogram: string
    accent?: string
}

const WHITE = '#fff'

const BRANDS: Record<ChannelEnum, ChannelBrand> = {
    [ChannelEnum.VTex]: {mark: 'vtex.png', color: '#F71963', textColor: WHITE, monogram: 'VT'},
    [ChannelEnum.Magento_1_9]: {color: '#EE672F', textColor: WHITE, monogram: 'M1'},
    [ChannelEnum.Tray]: {mark: 'tray.png', color: '#0F65A8', textColor: WHITE, monogram: 'TR'},
    [ChannelEnum.B2W]: {mark: 'americanas.png', color: '#E60014', textColor: WHITE, monogram: 'B2'},
    [ChannelEnum.Muven_Cart]: {mark: 'muven-cart.png', color: '#89138A', textColor: WHITE, monogram: 'MC'},
    [ChannelEnum.LojaIntegrada]: {mark: 'loja-integrada.png', color: '#21B5B5', textColor: WHITE, monogram: 'LI'},
    [ChannelEnum.MagazineLuiza]: {mark: 'magalu.png', color: '#0086FF', textColor: WHITE, monogram: 'MG'},
    [ChannelEnum.ViaVarejo]: {mark: 'casas-bahia.png', color: '#1E3FCB', textColor: WHITE, monogram: 'VV'},
    [ChannelEnum.Azata]: {color: '#3D4A5C', textColor: WHITE, monogram: 'AZ'},
    [ChannelEnum.TrayFbits]: {mark: 'tray.png', color: '#0F65A8', textColor: WHITE, monogram: 'TF'},
    [ChannelEnum.MercadoLivre]: {mark: 'mercado-livre.png', color: '#FFE600', textColor: '#2D3277', monogram: 'ML'},
    [ChannelEnum.WooCommerce]: {mark: 'woocommerce.png', color: '#7F54B3', textColor: WHITE, monogram: 'WC'},
    [ChannelEnum.OpenCart]: {color: '#23A1D1', textColor: WHITE, monogram: 'OC'},
    [ChannelEnum.JetNeo]: {color: '#00A859', textColor: WHITE, monogram: 'JN'},
    [ChannelEnum.Custom]: {color: 'var(--tint-brand-bg)', textColor: 'var(--brand)', monogram: 'CP'},
    [ChannelEnum.NuvemShop]: {mark: 'nuvemshop.png', color: '#2C3357', textColor: WHITE, monogram: 'NS'},
    [ChannelEnum.Magento_2_3]: {color: '#EE672F', textColor: WHITE, monogram: 'M2'},
    [ChannelEnum.Shopify]: {mark: 'shopify.png', color: '#95BF47', textColor: WHITE, monogram: 'SF'},
    [ChannelEnum.Shopee]: {mark: 'shopee.png', color: '#EE4D2D', textColor: WHITE, monogram: 'SP'},
    [ChannelEnum.Netshoes]: {color: '#5E2B97', textColor: WHITE, monogram: 'NT'},
    [ChannelEnum.Dafiti]: {color: '#000000', textColor: WHITE, monogram: 'DF'},
    [ChannelEnum.Flexy]: {mark: 'flexy.png', color: '#1F3A5F', textColor: WHITE, monogram: 'FX'},
    [ChannelEnum.WBuy]: {mark: 'wbuy.png', color: '#F2542D', textColor: WHITE, monogram: 'WB'},
    [ChannelEnum.Amazon]: {mark: 'amazon.png', color: '#232F3E', textColor: '#FF9900', monogram: 'AM', accent: '#FF9900'},
    [ChannelEnum.Shein]: {mark: 'shein.png', color: '#000000', textColor: WHITE, monogram: 'SH'},
    [ChannelEnum.TiktokShop]: {mark: 'tiktok-shop.png', color: '#000000', textColor: WHITE, monogram: 'TT', accent: '#FE2C55'},
}

// Canal que ainda não está no mapa (ex.: enum novo no commons): tile neutro em
// vez de quebrar a célula.
const FALLBACK: ChannelBrand = {color: 'var(--surface-3)', textColor: 'var(--text-muted)', monogram: '?'}

export function getChannelBrand(en: ChannelEnum | undefined | null): ChannelBrand {
    if (en === undefined || en === null) {
        return FALLBACK
    }

    return BRANDS[en] || FALLBACK
}

// Abaixo disso a cor é preto/quase preto: como luz de fundo vira mancha cinza.
const MIN_GLOW_LUMINANCE = .02

function relativeLuminance(hex: string): number | null {
    const m = /^#([0-9a-f]{6})$/i.exec(hex)

    if (!m) {
        return null
    }

    const [r, g, b] = [0, 2, 4].map((i) => {
        const c = parseInt(m[1].substr(i, 2), 16) / 255

        return c <= .03928 ? c / 12.92 : Math.pow((c + .055) / 1.055, 2.4)
    })

    return .2126 * r + .7152 * g + .0722 * b
}

/**
 * Cor que "ilumina" o hero do canal. `null` quando a marca não tem uma cor
 * aproveitável (preta, token de tema): quem chama cai na cor do Muven.
 */
export function getChannelGlow(brand: ChannelBrand): string | null {
    if (brand.accent) {
        return brand.accent
    }

    const luminance = relativeLuminance(brand.color)

    if (luminance === null || luminance < MIN_GLOW_LUMINANCE) {
        return null
    }

    return brand.color
}
